import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { scenariosAPI, structuredInputsAPI, marketAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useModules } from '../../lib/modules-context';
import { useProject } from '../../lib/project-context';
import { WizardFooter, WizardProgressBar } from './WizardFooter';
import {
  Building2, MapPin, Layers, HardHat, Receipt,
  CalendarClock, TrendingUp, Banknote, Calculator, Landmark,
  CheckCircle2, ChevronLeft, ChevronRight, Play,
  ArrowLeft, Loader2, ShieldCheck, Sparkles, Sliders, BookmarkPlus, HelpCircle
} from 'lucide-react';
import {
  STEPS,
  StepGeneral, StepTypologies, StepLand, StepBudget, StepIndirect,
  StepSchedule, StepSales, StepCollection, StepFinancing, StepAssumptions, StepReview,
} from './steps';
import { getWizardMode, setWizardMode } from './wizard-tips';
import { WizardValidationBanner, WizardHealthScore, buildIssues, useRunHorizon } from './WizardValidation';
import { WizardCopilot } from './WizardCopilot';
import { SaveTemplateDialog } from '../../components/SaveTemplateDialog';
import { WizardTour, resetWizardTour } from '../../components/WizardTour';

const STEP_DESCRIPTIONS = {
  general: 'Ficha técnica del desarrollo. Define identidad, ubicación, formato y superficies.',
  typologies: 'Mezcla de producto. Cada fila es una tipología con precio, costo y absorción.',
  land: 'Costo del terreno, calendario de pagos y gastos preoperativos.',
  budget: 'Presupuesto de construcción desglosado por partida.',
  indirect: 'Gastos indirectos: administración, marketing, supervisión, seguros, etc.',
  schedule: 'Cronograma general del proyecto: inicio de obra, comercialización y entregas.',
  sales: 'Variables comerciales: absorción, preventa, escalación, entregas.',
  collection: 'Estructura de cobranza: enganche, parcialidades, escrituración, morosidad.',
  financing: 'Tipo de crédito, tasa, comisiones y estructura de financiamiento.',
  assumptions: 'Supuestos macroeconómicos y parámetros de sensibilidad.',
  review: 'Revisa que todo esté completo antes de ejecutar el análisis.',
};

export default function ScenarioWizardPage() {
  const { projectId, scenarioId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isActive } = useModules();
  const [currentStep, setCurrentStep] = useState(0);
  const runHorizon = useRunHorizon();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [assumptions, setAssumptions] = useState({});
  const [structured, setStructured] = useState({});
  const [scenarioName, setScenarioName] = useState('');
  const [budgetMode, setBudgetMode] = useState(null);
  const [indirectMode, setIndirectMode] = useState(null);
  const [scheduleMode, setScheduleMode] = useState(null);
  const [salesMode, setSalesMode] = useState(null);
  const [marketSuggestions, setMarketSuggestions] = useState({});
  const [wizardMode, setWizardModeState] = useState(() => getWizardMode());
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);

  const toggleWizardMode = () => {
    const next = wizardMode === 'guided' ? 'power' : 'guided';
    setWizardModeState(next);
    setWizardMode(next);
    toast.success(next === 'guided' ? 'Modo Guiado activado — se ocultan campos avanzados' : 'Modo Power activado — todos los campos visibles');
  };

  useEffect(() => {
    if (assumptions && Object.keys(assumptions).length > 0) {
      if (!budgetMode && assumptions.budget_capture_mode) setBudgetMode(assumptions.budget_capture_mode);
      if (!indirectMode && assumptions.indirect_capture_mode) setIndirectMode(assumptions.indirect_capture_mode);
    }
  }, [assumptions?.budget_capture_mode, assumptions?.indirect_capture_mode]);

  const saveTimer = useRef(null);
  const pendingUpdates = useRef({});

  const loadData = useCallback(async () => {
    try {
      const [scenarioRes, structuredRes] = await Promise.all([
        scenariosAPI.get(projectId, scenarioId),
        structuredInputsAPI.getAll(scenarioId),
      ]);
      const sc = scenarioRes.data;
      setScenarioName(sc.name);
      const flat = sc.assumptions || {};
      flat._detail = sc.assumptions_detail || [];
      setAssumptions(flat);
      setStructured(structuredRes.data);

      // Load market suggestions (non-blocking) — only if market module is active
      if (isActive('market')) {
        marketAPI.getSuggestions(projectId, scenarioId)
          .then(r => { if (r.data?.has_suggestions) setMarketSuggestions(r.data.suggestions); })
          .catch(() => {});
      }
    } catch {
      toast.error('Error al cargar datos del escenario');
    }
    setLoading(false);
  }, [projectId, scenarioId]);

  useEffect(() => { loadData(); }, [loadData]);

  // When activeScenario changes via dropdown, navigate to the new scenario's wizard URL
  const { activeScenario } = useProject();
  useEffect(() => {
    if (activeScenario?.id && activeScenario.id !== scenarioId) {
      navigate(`/analyzer/wizard/${projectId}/${activeScenario.id}`, { replace: true });
    }
  }, [activeScenario?.id]);

  // Navigate to step from ?step= query param (e.g. from Decision Engine)
  useEffect(() => {
    const stepParam = searchParams.get('step');
    if (stepParam && !loading) {
      const idx = STEPS.findIndex(s => s.id === stepParam);
      if (idx >= 0) setCurrentStep(idx);
    }
  }, [searchParams, loading]);

  const handleAssumptionChange = useCallback((key, value) => {
    setAssumptions(prev => ({ ...prev, [key]: value }));
    pendingUpdates.current[key] = value;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const updates = { ...pendingUpdates.current };
      pendingUpdates.current = {};
      const updateList = Object.entries(updates).map(([k, v]) => ({ key: k, value: typeof v === 'string' ? v : Number(v) }));
      if (updateList.length === 0) return;
      setSaving(true);
      try { await scenariosAPI.updateAssumptions(projectId, scenarioId, updateList); }
      catch { toast.error('Error al guardar'); }
      setSaving(false);
    }, 800);
  }, [projectId, scenarioId]);

  const reloadStructured = useCallback(async () => {
    try { const res = await structuredInputsAPI.getAll(scenarioId); setStructured(res.data); } catch (err) { console.error('[ScenarioWizard] reloadStructured failed', err); }
  }, [scenarioId]);

  const handleRunAnalysis = () => {
    // Sprint 6 · Bloqueo por críticos
    const issues = buildIssues(assumptions, structured, runHorizon);
    const criticals = issues.filter((i) => i.severity === 'critical');
    if (criticals.length > 0) {
      toast.error(`No se puede ejecutar: ${criticals.length} error${criticals.length > 1 ? 'es' : ''} crítico${criticals.length > 1 ? 's' : ''}. Revisa el paso "${STEPS.find(s => s.id === criticals[0].step)?.label || 'Revisión'}".`);
      const idx = STEPS.findIndex((s) => s.id === criticals[0].step);
      if (idx >= 0) setCurrentStep(idx);
      return;
    }
    // L-90 · Terminar de capturar no es decidir que esa corrida cita el comité.
    // Llamaba a /analyzer/run —que PERSISTE— y descartaba el resultado por
    // completo, ni siquiera duration_ms: dejaba una corrida registrada que el
    // usuario no pidió y computaba dos veces, porque el Analyzer previsualiza al
    // montarse (Analyzer.jsx:301). Registrar es del botón explícito de SWA-244,
    // a un clic en la pantalla a la que este botón lleva. El bloqueo por
    // críticos de arriba es independiente: no dependía de la llamada.
    navigate(`/analyzer?project=${projectId}&scenario=${scenarioId}`);
  };

  const step = STEPS[currentStep];
  const progressPct = ((currentStep + 1) / STEPS.length) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center space-y-3">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: 'var(--primary-hex)' }} />
          <p className="text-sm text-muted-foreground">Cargando escenario...</p>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    const common = { assumptions, onChange: handleAssumptionChange };
    switch (step.id) {
      case 'general': return <StepGeneral {...common} structured={structured} />;
      case 'typologies': return <StepTypologies typologies={structured.typologies || []} scenarioId={scenarioId} onReload={reloadStructured} {...common} />;
      case 'land': return <StepLand {...common} landSchedule={structured.land_schedule || []} scenarioId={scenarioId} onReload={reloadStructured} />;
      case 'budget': return <StepBudget {...common} budgetItems={structured.budget_items || []} scenarioId={scenarioId} onReload={reloadStructured} structured={structured} mode={budgetMode || 'general'} setMode={setBudgetMode} />;
      case 'indirect': return <StepIndirect {...common} indirectCosts={structured.indirect_costs || []} scenarioId={scenarioId} onReload={reloadStructured} mode={indirectMode || 'general'} setMode={setIndirectMode} />;
      case 'schedule': return <StepSchedule {...common} mode={scheduleMode || 'general'} setMode={setScheduleMode} />;
      case 'sales': return <StepSales {...common} structured={structured} mode={salesMode || 'general'} setMode={setSalesMode} marketSuggestions={marketSuggestions} />;
      case 'collection': return <StepCollection {...common} collectionStructure={structured.collection_structure || []} scenarioId={scenarioId} onReload={reloadStructured} />;
      case 'financing': return <StepFinancing {...common} scenarioId={scenarioId} projectId={projectId} />;
      case 'assumptions': return <StepAssumptions {...common} />;
      case 'review': return <StepReview assumptions={assumptions} structured={structured} />;
      default: return null;
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="scenario-wizard-page">
      {/* Top bar */}
      <div className="shrink-0 border-b border-border bg-card/80 backdrop-blur px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/analyzer?project=${projectId}&scenario=${scenarioId}`)} data-testid="wizard-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-sm font-display font-semibold">{scenarioName}</h1>
              <p className="text-xs text-muted-foreground">Paso {currentStep + 1} de {STEPS.length} — {step.label}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <WizardHealthScore
              assumptions={assumptions}
              structured={structured}
              onGoToStep={(stepId) => { const i = STEPS.findIndex((s) => s.id === stepId); if (i >= 0) setCurrentStep(i); }}
            />
            <button
              onClick={resetWizardTour}
              className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-muted/40 transition-colors"
              data-testid="replay-tour-btn"
              title="Ver tour de onboarding"
            >
              <HelpCircle className="h-3.5 w-3.5" style={{ color: '#94A3B8' }} />
            </button>
            <button
              onClick={() => setSaveTemplateOpen(true)}
              className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all border hover:bg-muted/40"
              style={{ borderColor: '#E2E8F0', color: '#4F46E5' }}
              data-testid="save-template-btn"
              title="Guardar este escenario como plantilla reusable"
            >
              <BookmarkPlus className="h-3.5 w-3.5" />
              Guardar plantilla
            </button>
            <button
              onClick={toggleWizardMode}
              className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all border"
              style={{
                background: wizardMode === 'guided' ? '#EEF2FF' : '#FEF3C7',
                borderColor: wizardMode === 'guided' ? '#6366F1' : '#F59E0B',
                color: wizardMode === 'guided' ? '#4F46E5' : '#B45309',
              }}
              data-testid="wizard-mode-toggle"
              title={wizardMode === 'guided' ? 'Cambiar a Modo Power (mostrar todo)' : 'Cambiar a Modo Guiado (ocultar avanzados)'}
            >
              {wizardMode === 'guided' ? <Sparkles className="h-3.5 w-3.5" /> : <Sliders className="h-3.5 w-3.5" />}
              {wizardMode === 'guided' ? 'Modo Guiado' : 'Modo Power'}
            </button>
            {saving ? (
              <Badge variant="secondary" className="text-xs gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Guardando...</Badge>
            ) : (
              <Badge variant="outline" className="text-xs gap-1.5"><ShieldCheck className="h-3 w-3" style={{ color: 'var(--success)' }} /> Guardado</Badge>
            )}
          </div>
        </div>
        <div className="mt-3">
          <WizardProgressBar steps={STEPS} currentStep={currentStep} assumptions={assumptions} structured={structured} onStepClick={setCurrentStep} />
        </div>
      </div>

      {/* Content area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Step sidebar */}
        <div className="hidden lg:flex flex-col w-[220px] shrink-0 border-r border-border bg-muted/20 py-4 px-3 overflow-y-auto">
          {STEPS.map((s, i) => {
            const StepIcon = s.icon;
            const isCurrent = i === currentStep;
            const isCompleted = i < currentStep;
            return (
              <button key={s.id} onClick={() => setCurrentStep(i)}
                className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-[background-color,color] duration-150 text-left w-full ${isCurrent ? 'font-medium' : isCompleted ? 'text-muted-foreground' : 'text-muted-foreground/60'}`}
                style={isCurrent ? { background: 'var(--primary-hex)', color: 'white' } : {}}
                data-testid={`wizard-step-${s.id}`}
              >
                <StepIcon className="h-4 w-4 shrink-0" />
                <span className="truncate">{s.shortLabel}</span>
                {isCompleted && <CheckCircle2 className="h-3.5 w-3.5 ml-auto shrink-0" style={{ color: isCurrent ? 'white' : 'var(--success)' }} />}
              </button>
            );
          })}
        </div>

        {/* Main form area */}
        <ScrollArea className="flex-1">
          <div className="max-w-[960px] mx-auto px-6 py-6">
            <AnimatePresence mode="wait">
              <motion.div key={step.id} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>
                <div className="mb-6">
                  <h2 className="text-xl font-display font-semibold tracking-tight">{step.label}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{STEP_DESCRIPTIONS[step.id]}</p>
                </div>
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>
        </ScrollArea>
      </div>

      {/* Bottom: totals + navigation */}
      <div className="shrink-0 border-t border-border bg-card/80 backdrop-blur">
        {/* Totals row */}
        <div className="py-2" style={{ borderBottom: '1px solid var(--border-default)', background: '#F8FAFC' }}>
          <div className="max-w-[960px] mx-auto">
            <WizardFooter stepId={step.id} assumptions={assumptions} structured={structured} />
          </div>
        </div>
        {/* Navigation row */}
        <div className="px-6 py-3">
          <div className="flex items-center justify-between max-w-[960px] mx-auto">
          <Button variant="outline" disabled={currentStep === 0} onClick={() => setCurrentStep(Math.max(0, currentStep - 1))} data-testid="wizard-prev-btn">
            <ChevronLeft className="h-4 w-4 mr-1.5" /> Anterior
          </Button>
          {currentStep < STEPS.length - 1 ? (
            <Button onClick={() => {
              // Validate required fields on Step 1
              if (currentStep === 0) {
                const missing = [];
                if (!assumptions?.project_type) missing.push('Tipo de desarrollo');
                if (!assumptions?.housing_format) missing.push('Formato habitacional');
                if (!assumptions?.market_segment) missing.push('Segmento de mercado');
                if (missing.length > 0) {
                  toast.error(`Completa: ${missing.join(', ')}`);
                  return;
                }
              }
              setCurrentStep(Math.min(STEPS.length - 1, currentStep + 1));
            }} data-testid="wizard-next-btn">
              Siguiente <ChevronRight className="h-4 w-4 ml-1.5" />
            </Button>
          ) : (
            <Button onClick={handleRunAnalysis} disabled={running} data-testid="wizard-run-analysis-btn"
              style={{ background: 'var(--primary-hex)' }} className="text-white hover:opacity-90">
              {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
              {running ? 'Ejecutando...' : 'Ejecutar Análisis'}
            </Button>
          )}
        </div>
        </div>
      </div>

      {/* Sprint 6 · Semana 2 — Copilot de Configuración */}
      <WizardCopilot
        projectId={projectId}
        scenarioId={scenarioId}
        onChangesApplied={loadData}
      />

      {/* Sprint 6 · Semana 3.2 — Guardar como plantilla */}
      <SaveTemplateDialog
        open={saveTemplateOpen}
        onOpenChange={setSaveTemplateOpen}
        projectId={projectId}
        scenarioId={scenarioId}
        scenarioName={scenarioName}
      />

      {/* Sprint 6 · Semana 3.3 — Onboarding Tour (5 pasos) */}
      <WizardTour />
    </div>
  );
}
